import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useWebSocket } from '../contexts/WebSocketContext';
import { 
  Bell, Check, CheckCheck, Clock, Trash2, AlertCircle, 
  MessageSquare, FileText, Video, BookOpen, Megaphone, 
  AlertTriangle, RefreshCcw, Eye, Archive, Inbox, Download, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const NotificationsPage = ({ onMenuClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [pendingAcknowledgements, setPendingAcknowledgements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const { updateUnreadCount } = useWebSocket();

  // Notification detail dialog
  const [detailNotif, setDetailNotif] = useState(null);
  const [detailContent, setDetailContent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const API_BASE = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');

  useEffect(() => {
    fetchNotifications();
    fetchPendingAcknowledgements();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      
      // Mettre à jour le compteur global
      const unreadCount = response.data.filter(n => !n.is_read).length;
      updateUnreadCount(unreadCount);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingAcknowledgements = async () => {
    try {
      const response = await api.get('/notifications/pending-acknowledgements');
      setPendingAcknowledgements(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des rappels:', error);
    }
  };

  // Marquer comme lu (notification consultée)
  const handleMarkAsRead = async (notifId) => {
    try {
      const response = await api.put(`/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notifId ? { ...n, is_read: true } : n
      ));
      
      // Mettre à jour le compteur via la réponse API
      if (response.data.unread_count !== undefined) {
        updateUnreadCount(response.data.unread_count);
      }
      
      // Emit event to refresh sidebar badges
      window.dispatchEvent(new CustomEvent('refreshBadges'));
      
      toast.success('Notification marquée comme lue');
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };

  // Ouvrir une notification (et son contenu lié, ex: annonce)
  const openNotification = async (notif) => {
    // Marquer comme lue si nécessaire
    if (!notif.is_read) {
      handleMarkAsRead(notif.id);
    }

    setDetailNotif(notif);
    setDetailContent(null);

    // Pour les annonces, récupérer le contenu complet + pièce jointe
    if (notif.type === 'nouvelle_annonce') {
      const announcementId = notif.extra_data?.announcement_id;
      setDetailLoading(true);
      try {
        const response = await api.get('/announcements');
        const found = announcementId
          ? response.data.find(a => a.id === announcementId)
          : null;
        setDetailContent(found || null);
      } catch (error) {
        setDetailContent(null);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  // Confirmer vu (acquitter explicitement un rappel)
  const handleAcknowledge = async (notifId) => {
    try {
      const response = await api.put(`/notifications/${notifId}/acknowledge`);
      setNotifications(prev => prev.map(n => 
        n.id === notifId ? { 
          ...n, 
          is_read: true, 
          is_acknowledged: true, 
          acknowledged_at: response.data.acknowledged_at 
        } : n
      ));
      setPendingAcknowledgements(prev => prev.filter(n => n.id !== notifId));
      
      // Mettre à jour le compteur via la réponse API
      if (response.data.unread_count !== undefined) {
        updateUnreadCount(response.data.unread_count);
      }
      
      toast.success('Rappel acquitté - traité avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'acquittement');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      updateUnreadCount(0);
      // Emit event to refresh sidebar badges
      window.dispatchEvent(new CustomEvent('refreshBadges'));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (notifId) => {
    try {
      await api.delete(`/notifications/${notifId}`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      setPendingAcknowledgements(prev => prev.filter(n => n.id !== notifId));
      toast.success('Notification supprimée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'nouveau_message':
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case 'nouveau_devoir':
      case 'devoir_corrige':
        return <FileText className="w-5 h-5 text-amber-500" />;
      case 'live_a_venir':
      case 'replay_disponible':
        return <Video className="w-5 h-5 text-emerald-500" />;
      case 'mise_a_jour_cours':
        return <BookOpen className="w-5 h-5 text-violet-500" />;
      case 'nouvelle_annonce':
        return <Megaphone className="w-5 h-5 text-purple-500" />;
      case 'rappel':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };

  const getPriorityBadge = (priority) => {
    switch(priority) {
      case 'urgent':
        return <Badge variant="destructive" className="ml-2">Urgent</Badge>;
      case 'important':
        return <Badge className="bg-amber-100 text-amber-700 ml-2">Important</Badge>;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    
    if (hours < 1) {
      const minutes = Math.floor(diff / 60000);
      return `Il y a ${minutes} min`;
    }
    if (hours < 24) {
      return `Il y a ${hours}h`;
    }
    if (hours < 48) {
      return 'Hier';
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Filtrage des notifications
  const unreadNotifications = notifications.filter(n => !n.is_read);
  const acknowledgedNotifications = notifications.filter(n => n.is_acknowledged);
  const requiresAckNotifications = notifications.filter(n => n.requires_acknowledgement && !n.is_acknowledged);
  
  const filteredNotifications = (() => {
    switch(activeTab) {
      case 'unread':
        return unreadNotifications;
      case 'pending':
        return requiresAckNotifications;
      case 'acknowledged':
        return acknowledgedNotifications;
      default:
        return notifications;
    }
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Notifications" onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Rappels en attente d'acquittement - Section prioritaire */}
        {pendingAcknowledgements.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-orange-500 bg-orange-50 dark:bg-orange-900/10" data-testid="pending-acknowledgements-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Rappels en attente de confirmation ({pendingAcknowledgements.length})
              </CardTitle>
              <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                Ces rappels nécessitent votre confirmation explicite
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingAcknowledgements.map((notif) => (
                  <div 
                    key={notif.id} 
                    className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-orange-200 dark:border-orange-800 shadow-sm" 
                    data-testid={`pending-ack-${notif.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {getNotificationIcon(notif.type)}
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{notif.title}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{notif.message}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatDate(notif.created_at)}
                        </p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                      onClick={() => handleAcknowledge(notif.id)}
                      data-testid={`confirm-seen-btn-${notif.id}`}
                    >
                      <CheckCheck className="w-4 h-4 mr-2" />
                      Confirmer vu
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Liste principale des notifications */}
        <Card data-testid="notifications-main-card">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-violet-600" />
                Mes Notifications
                {unreadNotifications.length > 0 && (
                  <Badge variant="destructive" className="ml-2" data-testid="unread-count-badge">
                    {unreadNotifications.length}
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={fetchNotifications} data-testid="refresh-btn">
                  <RefreshCcw className="w-4 h-4" />
                </Button>
                {unreadNotifications.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleMarkAllAsRead} data-testid="mark-all-read-btn">
                    <Check className="w-4 h-4 mr-1" />
                    Tout marquer lu
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4 grid grid-cols-4 w-full">
                <TabsTrigger value="all" data-testid="tab-all" className="flex items-center gap-1">
                  <Inbox className="w-4 h-4" />
                  <span className="hidden sm:inline">Toutes</span>
                  <span className="text-xs">({notifications.length})</span>
                </TabsTrigger>
                <TabsTrigger value="unread" data-testid="tab-unread" className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span className="hidden sm:inline">Non lues</span>
                  <span className="text-xs">({unreadNotifications.length})</span>
                </TabsTrigger>
                <TabsTrigger value="pending" data-testid="tab-pending" className="flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="hidden sm:inline">À traiter</span>
                  <span className="text-xs">({requiresAckNotifications.length})</span>
                </TabsTrigger>
                <TabsTrigger value="acknowledged" data-testid="tab-acknowledged" className="flex items-center gap-1">
                  <Archive className="w-4 h-4" />
                  <span className="hidden sm:inline">Traités</span>
                  <span className="text-xs">({acknowledgedNotifications.length})</span>
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[500px]">
                {filteredNotifications.length === 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="empty-state">
                    <Bell className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>
                      {activeTab === 'unread' && 'Aucune notification non lue'}
                      {activeTab === 'pending' && 'Aucun rappel en attente'}
                      {activeTab === 'acknowledged' && 'Aucune notification acquittée'}
                      {activeTab === 'all' && 'Aucune notification'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredNotifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-4 rounded-lg border transition-all ${
                          notif.is_acknowledged 
                            ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800'
                            : notif.is_read 
                              ? 'bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700' 
                              : 'bg-violet-50 dark:bg-violet-900/10 border-violet-200 dark:border-violet-800'
                        }`}
                        data-testid={`notification-${notif.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="mt-1">
                            {getNotificationIcon(notif.type)}
                          </div>
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => openNotification(notif)}
                            data-testid={`open-notification-${notif.id}`}
                          >
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                              <h4 className={`font-medium ${notif.is_read ? 'text-slate-700 dark:text-slate-300' : 'text-slate-900 dark:text-white'}`}>
                                {notif.title}
                              </h4>
                              {getPriorityBadge(notif.priority)}
                              
                              {/* Badge d'état */}
                              {notif.is_acknowledged ? (
                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                  <CheckCheck className="w-3 h-3 mr-1" />
                                  Traité
                                </Badge>
                              ) : notif.requires_acknowledgement ? (
                                <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  À confirmer
                                </Badge>
                              ) : !notif.is_read ? (
                                <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                                  Nouveau
                                </Badge>
                              ) : null}
                            </div>
                            
                            <p className="text-sm text-slate-600 dark:text-slate-400">{notif.message}</p>
                            
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(notif.created_at)}
                              </span>
                              {notif.is_acknowledged && notif.acknowledged_at && (
                                <span className="flex items-center gap-1 text-emerald-600">
                                  <CheckCheck className="w-3 h-3" />
                                  Confirmé le {new Date(notif.acknowledged_at).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* Ouvrir / consulter la notification */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openNotification(notif)}
                              title="Ouvrir"
                              data-testid={`open-btn-${notif.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {/* Confirmer vu - seulement si requires_acknowledgement et non acquitté */}
                            {notif.requires_acknowledgement && !notif.is_acknowledged && (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleAcknowledge(notif.id)}
                                data-testid={`confirm-btn-${notif.id}`}
                              >
                                <CheckCheck className="w-4 h-4 mr-1" />
                                Confirmer
                              </Button>
                            )}
                            
                            {/* Supprimer */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(notif.id)}
                              title="Supprimer"
                              className="text-slate-400 hover:text-red-500"
                              data-testid={`delete-btn-${notif.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </Tabs>
          </CardContent>
        </Card>

        {/* Légende des états */}
        <Card className="mt-6">
          <CardContent className="py-4">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Légende des états</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-violet-500"></div>
                <span className="text-slate-600 dark:text-slate-400">Non lue - Nouvelle notification</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span className="text-slate-600 dark:text-slate-400">À confirmer - Rappel en attente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-slate-600 dark:text-slate-400">Traité - Confirmation reçue</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification detail dialog */}
        <Dialog open={!!detailNotif} onOpenChange={(open) => { if (!open) { setDetailNotif(null); setDetailContent(null); } }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden">
            <DialogHeader className="pr-6">
              <DialogTitle className="flex items-center gap-2">
                {detailNotif && getNotificationIcon(detailNotif.type)}
                {detailContent?.title || detailNotif?.title}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2 overflow-y-auto max-h-[calc(85vh-9rem)] pr-1">
              {detailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
                </div>
              ) : detailContent ? (
                <>
                  <div className="flex items-center flex-wrap gap-2 text-xs text-slate-500">
                    <span>{detailContent.author_name}</span>
                    {detailContent.created_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(detailContent.created_at).toLocaleString('fr-FR')}
                      </span>
                    )}
                    {detailContent.target_info && (
                      <Badge variant="outline">{detailContent.target_info}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                    {detailContent.content}
                  </p>
                  {detailContent.attachment_url && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => window.open(`${API_BASE}${detailContent.attachment_url}`, '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {detailContent.attachment_name || 'Télécharger la pièce jointe'}
                    </Button>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {detailNotif?.message}
                </p>
              )}

              {detailNotif?.action_url && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.open(detailNotif.action_url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ouvrir le lien
                </Button>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setDetailNotif(null); setDetailContent(null); }}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default NotificationsPage;
